/* eslint-disable max-len */
import { forwardRef, useEffect, useState } from 'react';
import { FormikErrors, FormikTouched } from 'formik';

import { Textarea } from '@/components/common/TextAreaTag4/TextareaTag4';
import styles from './party-headers.module.scss';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import { Customer } from '@/pages/AddContract/AddContract.container';
import ContractHeaderItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';

export interface IPartyColumnProps {
  fieldName: string;
  fieldKey: string;
  customerParty: ContractCommonData['customer'] | undefined | null;
  isEditable: boolean;
  isRequired: boolean;
  touched: FormikTouched<any>;
  errors: FormikErrors<any>;
  setFieldTouched: any;
  setFieldValue: any;
  searchCus:
  | {
    searchCustomers: (text: string) => void;
    isSearchLoading: boolean;
    searchCustomersData: Array<
    {
      customerID: string;
      customerName: string;
      customerAccountNumber: string;
      address?: string | null;
      postCode?: string | null;
    } & { __typename: 'CustomerType' }
    >;
  }
  | undefined;
  isLoadingContractDetails?: boolean;
}

const PartyColumn = forwardRef<any, IPartyColumnProps>((props, ref) => {
  const {
    fieldName,
    fieldKey,
    customerParty,
    isEditable,
    isRequired,
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    searchCus,
    isLoadingContractDetails,
  } = props;

  const [currentCustomerParty, setCurrentCustomerParty] = useState<
  ContractCommonData['customer'] | undefined | null
  >(undefined);

  useEffect(() => {
    if (!isLoadingContractDetails) {
      setCurrentCustomerParty(customerParty);
    }
  }, [isLoadingContractDetails]);

  return (
    <div className={styles['party-column']}>
      <div className={styles['party-column-header']}>
        <span>{fieldName}</span>
      </div>
      <hr style={{ marginBottom: '10px' }} />
      <div className={styles['dropdown-margin']}>
        <ContractDropdownHeaderItem<Customer>
          ref={ref}
          required={isRequired}
          label="Name"
          id={fieldKey}
          defaultValue={
            currentCustomerParty
              ? {
                customerID: currentCustomerParty!.customerID,
                customerName: currentCustomerParty!.customerName,
                customerAccountNumber:
                  currentCustomerParty!.customerAccountNumber,
                address: currentCustomerParty!.address,
                postCode: currentCustomerParty!.postCode,
                __typename: 'CustomerType',
              }
              : undefined
          }
          placeholder={currentCustomerParty?.customerName}
          getOptionLabel={(option) => option.customerName}
          getFormatOptionLabel={(option, optionLabelMeta) => (optionLabelMeta.context === 'value'
            ? `${option.customerName}`
            : `[${option.customerAccountNumber}] - ${option.customerName} ${option.postCode ? `, ${option.postCode}` : ''}`)}
          getOptionValue={(option) => `[${option.customerID}] - ${option.customerAccountNumber} - ${option.postCode ?? ''}`}
          handleInputChange={(value) => searchCus?.searchCustomers(value)}
          optionsData={searchCus?.searchCustomersData || []}
          handleChange={(option) => {
            const value = option ?? customerParty;
            setCurrentCustomerParty(value);
            setFieldTouched(fieldKey);
            setFieldValue(fieldKey, value);
          }}
          isLoading={searchCus?.isSearchLoading}
          errorMessage={
            touched[fieldKey] && errors[fieldKey] ? errors[fieldKey] : false
          }
          isSearchable={true}
          disabled={!isEditable}
          isClearable={true}
        />
      </div>
      <div className={styles['contract-header-container']}>
        <ContractHeaderItem
          isLoading={isLoadingContractDetails}
          label="Code"
          value={currentCustomerParty?.customerAccountNumber ?? ''}
          disabled
          inputStyle={{ width: '100%' }}
        />
      </div>
      <div className={styles['contract-header-container']}>
        <span className={styles['contract-label']}>Address</span>
        <Textarea
          name='Address'
          autoComplete='Address'
          className={styles['address-textarea']}
          value={
            [
              currentCustomerParty?.address,
              currentCustomerParty?.town,
              currentCustomerParty?.state,
              currentCustomerParty?.postCode,
              currentCustomerParty?.country,
            ]
              .filter(Boolean)
              .join('\n')
          }
          disabled={true}
          resize='none'
          wrap='off'
        />
      </div>

    </div>
  );
});

export default PartyColumn;
